/**
 * useJourneySignals
 *
 * Gathers the facts behind the journey map's sub-lines, so each of the three
 * screens that renders the map asks one question instead of eight.
 *
 * Every query it opens is gated on `enabled`. The map lives behind a modal on
 * all three screens, so without that gate this hook would fetch a patient's
 * documents, appointments, specimens and monitoring on every render of Home and
 * of the profile — for a dialog most sessions never open.
 */

import { useMemo } from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { usePhases } from "@src/packages/patients/hooks/usePhases";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import { useMedicationLogs } from "@src/packages/care/hooks/useMedicationLogs";
import { useCycleMonitoringEncounters } from "@src/packages/care/hooks/useCycleMonitoring";
import { useSpecimens } from "@src/packages/care/cryovault/useSpecimens";
import { useTestResults } from "@src/packages/test-results/hooks/useTestResults";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { isPerformedProcedurePhase } from "../presenters/performedProcedure";
import {
  NO_JOURNEY_SIGNALS,
  type JourneySignals,
} from "../presenters/journeyMilestones";
import type { Cycle } from "./useCycles";

type Options = {
  /** The cycle the map is describing — usually the most recent one. */
  cycle: Pick<Cycle, "id" | "startDate" | "outcome"> | null | undefined;
  /** Every cycle on file, to tell whether a later one exists. */
  cycles: Pick<Cycle, "id" | "startDate" | "status">[] | undefined;
  /** Her stated goal, which is the only signal the map reads off the patient. */
  hasGoal: boolean;
  /** False while the map is closed — see the note above. */
  enabled: boolean;
};

/** Procedure phases that mean the intervention itself has happened. */
const PERFORMED_PHASE_TYPES = new Set([
  "egg_retrieval",
  "transfer",
  "insemination",
]);

export function useJourneySignals({
  cycle,
  cycles,
  hasGoal,
  enabled,
}: Options): JourneySignals {
  const cycleId = enabled ? cycle?.id : undefined;

  const { data: patient } = usePatient();
  const { data: phases } = usePhases(cycleId);
  const { data: careTeam } = useCareTeam(cycleId);
  const { data: monitoring } = useCycleMonitoringEncounters(cycleId);
  const { data: specimens } = useSpecimens(undefined, { enabled });
  const { data: appointments } = useAppointments({ enabled });
  const { data: documents } = useDocuments({ enabled });
  const { data: testResults } = useTestResults({ enabled });
  // Bounded to this cycle: a dose logged during a cycle two years ago says
  // nothing about the protocol she is following now.
  const { data: medicationLogs } = useMedicationLogs({
    startDate: cycle?.startDate ?? undefined,
    enabled: enabled && !!cycle?.startDate,
  });

  return useMemo(() => {
    if (!enabled) return NO_JOURNEY_SIGNALS;

    const specimenList = specimens ?? [];
    const todayIso = new Date().toISOString().slice(0, 10);
    /**
     * A cycle started after the one that reached the closing rung.
     *
     * Measured from the newest *closed* cycle, not from the cycle this map
     * describes. Both callers pass the newest cycle as `cycle`, so asking for
     * something newer than the newest was asking for a cycle strictly after the
     * maximum — never true, which left "start your next cycle" permanently
     * unticked however many she had started.
     */
    const closedCycles = (cycles ?? []).filter(
      (candidate) => candidate.status === "closed" && !!candidate.startDate,
    );
    const newestClosed = closedCycles.reduce<
      (typeof closedCycles)[number] | null
    >(
      (latest, candidate) =>
        !latest?.startDate || candidate.startDate! > latest.startDate
          ? candidate
          : latest,
      null,
    );
    const hasLaterCycle = newestClosed
      ? (cycles ?? []).some(
          (candidate) =>
            !!candidate.startDate &&
            candidate.startDate > newestClosed.startDate!,
        )
      : false;
    return {
      hasGoal,
      // "a first document or result" — either satisfies the line it ticks.
      hasDocument:
        (documents?.length ?? 0) > 0 || (testResults?.length ?? 0) > 0,
      hasAppointment: (appointments?.length ?? 0) > 0,
      hasCareTeam: (careTeam?.length ?? 0) > 0,
      hasTestResult: (testResults?.length ?? 0) > 0,
      // The medications themselves, not the phase that would carry them. This
      // read `phase.id`, which every phase has — so "begin prescribed
      // medications" was ticked by a cycle merely having a plan.
      hasMedication: (phases ?? []).some(
        (phase) => (phase.patientMedications ?? []).length > 0,
      ),
      hasMedicationLog: (medicationLogs?.length ?? 0) > 0,
      partnerConnected: !!patient?.partnerId,
      hasMonitoring: (monitoring?.length ?? 0) > 0,
      // `isPerformedProcedurePhase` already holds this rule and is used across
      // the cycle screens: a start date alone is a booking, and equal dates are
      // the explicit-skip convention. Testing `!!startDate` here ticked "egg
      // retrieval or transfer" for a transfer booked for next Tuesday, and for
      // a retrieval that was skipped.
      hasPerformedProcedure: (phases ?? []).some(
        (phase) =>
          !!phase.type &&
          PERFORMED_PHASE_TYPES.has(phase.type) &&
          isPerformedProcedurePhase(phase),
      ),
      // Recovery is under way from the day the phase starts, so this one does
      // not wait for a completion window — but it still refuses a phase that is
      // only planned, or one skipped with equal dates.
      hasLutealPhase: (phases ?? []).some(
        (phase) =>
          phase.type === "luteal_phase" &&
          !!phase.startDate &&
          phase.startDate <= todayIso &&
          phase.endDate !== phase.startDate,
      ),
      hasSpecimen: specimenList.length > 0,
      hasStorageFacility: specimenList.some((specimen) =>
        specimen.storageLocations?.some((location) => !!location.facility),
      ),
      hasLaterCycle,
      // A discontinued cycle is written with `type: "cancelled"`, which is an
      // ending but not a result — ticking "record your beta hCG result" for a
      // patient whose transfer never happened.
      hasRecordedOutcome:
        !!cycle?.outcome?.type && cycle.outcome.type !== "cancelled",
    };
  }, [
    enabled,
    hasGoal,
    documents,
    appointments,
    careTeam,
    testResults,
    phases,
    medicationLogs,
    patient?.partnerId,
    monitoring,
    specimens,
    cycles,
    cycle?.outcome?.type,
  ]);
}
